import Pagination from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { cleanText } from '@/utils/sanitize';
import Modal from '@/components/ui/Modal';
import CertificationBulkImportModal from '@/components/certification-assignment/CertificationBulkImportModal';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import {
  AlertCircle,
  Award,
  Briefcase,
  Building,
  Calendar,
  Clock,
  Eye,
  ExternalLink,
  Info,
  Mail,
  Search,
  Send,
  Shield,
  Sparkles,
  TrendingUp,
  Upload,
  User as UserIcon,
  Users,
  X,
  XCircle,
  Medal,
  Clipboard
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '@/components/ui/Toast';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Textarea } from '@/components/ui/Textarea';
import { courseAssignmentDataService } from '@/services/course-assignment.service';
import { userService } from '@/services/user.service';
import { certificationService } from '@/services/certification.service';
import { certificationAssignmentService } from '@/services/certification-assignment.service';
import apiClient from '@/services/api';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { CERTIFICATION_ASSIGNMENT_PERMISSION, CERTIFICATION_ASSIGNMENT_ANALYTICS_PERMISSION } from '@/constants/constant-permissions';
import { ADMIN_CERTIFICATION_ASSIGNMENT_ANALYTICS } from '@/constants/constant-routes';

// Types
interface Certification {
  _id: string;
  id: string;
  certification_id: string; // The actual ID to use for API calls
  title: string;
  description: string;
  difficulty: string;
  question_count?: number;
  thumbnail_image_url?: string;
  banner_image_url?: string;
  passing_score?: number;
  time_limit?: number;
}

interface User {
  _id: string;
  given_name: string;
  family_name: string;
  full_name?: string;
  email: string;
  employee_id?: string;
  groups?: any[];
  certifications?: {
    assigned?: number;
    completed?: number;
  };
}

interface Group {
  _id: string;
  group_name: string;
  group_key: string;
  group_description?: string;
  parent_group_key?: string;
  createdAt: string;
}

interface Role {
  _id: string;
  role_name: string;
  role_key: string;
  role_description?: string;
  createdAt: string;
}

interface AssignmentSettings {
  is_mandatory: boolean;
  send_notification: boolean;
  enable_automatic_reminder: boolean;
  reminder_frequency_days: number;
  deadline_days?: number;
  message?: string;
}

interface FilterFormData {
  recipientDepartmentFilter: string;
  selectedRoleFilter: string;
  roleAssignmentDepartmentFilter: string;
  roleAssignmentRoleFilter: string;
  reminderFrequency: number;
  assignmentMessage: string;
}

export default function CertificationAssignment() {
  // Permission guards
  usePermissionRouteAccess([
    CERTIFICATION_ASSIGNMENT_PERMISSION.CERTIFICATION_ASSIGNMENT_READ,
    CERTIFICATION_ASSIGNMENT_PERMISSION.CERTIFICATION_ASSIGNMENT_WRITE,
  ]);
  const canWrite = useHasPermission([CERTIFICATION_ASSIGNMENT_PERMISSION.CERTIFICATION_ASSIGNMENT_WRITE]);
  const canViewAnalytics = useHasPermission([
    CERTIFICATION_ASSIGNMENT_ANALYTICS_PERMISSION.CERTIFICATION_ASSIGNMENT_ANALYTICS_READ,
    CERTIFICATION_ASSIGNMENT_ANALYTICS_PERMISSION.CERTIFICATION_ASSIGNMENT_ANALYTICS_WRITE,
  ]);

  // Initialize react-hook-form for select components
  const { control, watch, setValue, getValues, trigger } = useForm<FilterFormData>({
    defaultValues: {
      recipientDepartmentFilter: 'all',
      selectedRoleFilter: 'all',
      roleAssignmentDepartmentFilter: '',
      roleAssignmentRoleFilter: '',
      reminderFrequency: 7,
      assignmentMessage: ''
    }
  });

  // Watch form values
  const recipientDepartmentFilterValue = watch('recipientDepartmentFilter');
  const selectedRoleFilterValue = watch('selectedRoleFilter');
  const roleAssignmentDepartmentFilter = watch('roleAssignmentDepartmentFilter');
  const roleAssignmentRoleFilter = watch('roleAssignmentRoleFilter');

  const [assignmentType, setAssignmentType] = useState<'individual' | 'department' | 'role' | 'bulk'>('individual');
  const [selectedCertifications, setSelectedCertifications] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<Array<{ group_key: string, role_key: string, role_name?: string }>>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Search and filter for recipients
  const [recipientSearchQuery, setRecipientSearchQuery] = useState('');
  const [availableDepartments, setAvailableDepartments] = useState<any[]>([]);
  const [availableRolesForDepartment, setAvailableRolesForDepartment] = useState<any[]>([]);
  const [availableRolesForRoleAssignment, setAvailableRolesForRoleAssignment] = useState<any[]>([]);

  // Refs to track previous values for role assignment - to detect actual user changes
  const prevRoleAssignmentRoleFilter = useRef<string>('');
  const prevRoleAssignmentDepartmentFilter = useRef<string>('');

  // Deadline state (days-based)
  const [deadlineDays, setDeadlineDays] = useState<number | undefined>(undefined);
  const [deadlineError, setDeadlineError] = useState('');

  const [assignmentSettings, setAssignmentSettings] = useState<AssignmentSettings>({
    is_mandatory: false,
    send_notification: true,
    enable_automatic_reminder: true,
    reminder_frequency_days: 7,
    deadline_days: undefined,
    message: undefined
  });

  // Cache selected certification data across pagination
  const selectedCertDataRef = useRef<Map<string, Certification>>(new Map());

  // Placeholder data arrays
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  // Cache selected user data across pagination/search (so preview modal always has full data)
  const selectedUserDataRef = useRef<Map<string, User>>(new Map());
  useEffect(() => {
    users.forEach(user => {
      if (selectedUsers.includes(user._id)) selectedUserDataRef.current.set(user._id, user);
    });
  }, [users, selectedUsers]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [, setRoles] = useState<Role[]>([]);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [, setRolesLoading] = useState(false);

  // Pagination states
  const [certificationPagination, setCertificationPagination] = useState({ page: 1, totalPages: 1, totalItems: 0, limit: 20 });
  const [userPagination, setUserPagination] = useState({ page: 1, totalPages: 1, totalItems: 0, limit: 20 });
  const [groupPagination, setGroupPagination] = useState({ page: 1, totalPages: 1, totalItems: 0, limit: 20 });
  const [rolePagination, setRolePagination] = useState({ page: 1, totalPages: 1, totalItems: 0, limit: 20 });

  const [recentAssignments, setRecentAssignments] = useState<any[]>([]);
  const [recentAssignmentsLoading, setRecentAssignmentsLoading] = useState(false);

  // Duplicate detection state
  const [duplicateCheckResult, setDuplicateCheckResult] = useState<any>(null);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const [skipDuplicates] = useState(true); // Default to skip duplicates

  // Bulk import state
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);

  // Assignment actions state
  const [selectedAssignment, setSelectedAssignment] = useState<any>(null);
  const [showAssignmentDetails, setShowAssignmentDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Certification details modal state
  const [selectedCertificationsModal, setSelectedCertificationsModal] = useState<{certifications: {_id: string, name: string}[], assignmentId: string} | null>(null);

  // User departments modal state
  const [selectedUserDepartments, setSelectedUserDepartments] = useState<{
    userName: string,
    email: string,
    groups: {group_key: string, group_name: string, roles?: {role_key: string, role_name: string}[]}[]
  } | null>(null);

  // Assigned users/groups/roles modal state for Recent Assignments
  const [selectedAssignedUsersModal, setSelectedAssignedUsersModal] = useState<{
    users?: {_id: string, name: string, email: string}[],
    groups?: {_id?: string, group_key?: string, name?: string, group_name?: string}[],
    roles?: {_id?: string, role_key?: string, name?: string, role_name?: string}[],
    type: 'USER' | 'GROUP' | 'ROLE'
  } | null>(null);

  // Skipped users modal state for Recent Assignments
  const [selectedSkippedUsersModal, setSelectedSkippedUsersModal] = useState<{
    skipped_users?: {user_id: string, user_email: string, user_name: string, certification_id: string, reason: string}[],
    skipped_groups?: {group_key: string, group_name: string, certification_id: string, reason: string}[],
    skipped_roles?: {group_key: string, role_key: string, role_name: string, certification_id: string, reason: string}[]
  } | null>(null);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<{id: string, title: string} | null>(null);

  // Load initial data
  useEffect(() => {
    loadCertifications();
    loadRecentAssignments();
    loadAllGroupsForFilter();
  }, []);

  // Reload certifications when search changes (with debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      setCertificationPagination(prev => ({ ...prev, page: 1 }));
      loadCertifications(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load recipients based on assignment type
  useEffect(() => {
    // Clear search queries when assignment type changes
    setRecipientSearchQuery('');
    setSearchQuery('');

    // Reset pagination when assignment type changes
    setUserPagination(prev => ({ ...prev, page: 1 }));
    setGroupPagination(prev => ({ ...prev, page: 1 }));
    setRolePagination(prev => ({ ...prev, page: 1 }));

    if (assignmentType === 'individual') {
      loadUsers();
    } else if (assignmentType === 'department') {
      loadGroups();
    } else if (assignmentType === 'bulk') {
      // Bulk import tab will show information page with button to open modal
      // Don't auto-open modal here
    }
  }, [assignmentType]);

  // Reload users when search/filter changes (with debounce)
  useEffect(() => {
    if (assignmentType === 'individual') {
      const timer = setTimeout(() => {
        setUserPagination(prev => ({ ...prev, page: 1 }));
        loadUsers(1);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [recipientSearchQuery, recipientDepartmentFilterValue, selectedRoleFilterValue]);

  // Reload groups when search changes (with debounce)
  useEffect(() => {
    if (assignmentType === 'department') {
      const timer = setTimeout(() => {
        setGroupPagination(prev => ({ ...prev, page: 1 }));
        loadGroups(1);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [recipientSearchQuery]);

  // Reload roles when search changes (with debounce)
  useEffect(() => {
    if (assignmentType === 'role') {
      const timer = setTimeout(() => {
        setRolePagination(prev => ({ ...prev, page: 1 }));
        loadRoles(1);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [recipientSearchQuery]);

  useEffect(() => {
    setRecipientSearchQuery('');
    setSearchQuery('');

    // Reset pagination when assignment type changes
    setUserPagination(prev => ({ ...prev, page: 1 }));
    setGroupPagination(prev => ({ ...prev, page: 1 }));
    setRolePagination(prev => ({ ...prev, page: 1 }));
  }, [assignmentType]);

  // Load roles for selected department
  useEffect(() => {
    if (recipientDepartmentFilterValue !== 'all') {
      loadRolesForDepartment(recipientDepartmentFilterValue);
    } else {
      setAvailableRolesForDepartment([]);
      setValue('selectedRoleFilter', 'all');
    }
  }, [recipientDepartmentFilterValue, setValue]);

  const loadAllGroupsForFilter = async () => {
    try {
      const response = await userService.getGroupsDropdown();
      if (response.success && response.data) {
        setAvailableDepartments(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load departments for filter');
    }
  };

  const loadRolesForDepartment = async (groupKey: string) => {
    try {
      const response = await apiClient.get(`/group-role-map/${groupKey}`);
      if (response.data.success && response.data.data) {
        const roles = response.data.data.roles || [];
        setAvailableRolesForDepartment(roles);
      }
    } catch (error) {
      console.error('Failed to load roles for department:', error);
      setAvailableRolesForDepartment([]);
    }
  };

  // Load roles for selected department in role assignment
  useEffect(() => {
    // IMPORTANT: Clear available roles FIRST to prevent auto-selection of previous role
    setAvailableRolesForRoleAssignment([]);

    // Always reset role filter when department changes - using undefined to fully reset
    setValue('roleAssignmentRoleFilter', '', { shouldValidate: false, shouldDirty: false });

    if (roleAssignmentDepartmentFilter && roleAssignmentDepartmentFilter !== '') {
      // Small delay to ensure form state is cleared before loading new roles
      const timeoutId = setTimeout(() => {
        loadRolesForRoleAssignment(roleAssignmentDepartmentFilter);
      }, 0);
      return () => clearTimeout(timeoutId);
    } else {
      // Don't clear selectedRoles - allow multi-department role selection
    }
  }, [roleAssignmentDepartmentFilter, setValue]);

  const loadRolesForRoleAssignment = async (groupKey: string) => {
    try {
      const response = await apiClient.get(`/group-role-map/${groupKey}`);
      if (response.data.success && response.data.data) {
        const roles = response.data.data.roles || [];
        setAvailableRolesForRoleAssignment(roles);
      }
    } catch (error) {
      console.error('Failed to load roles for role assignment:', error);
      setAvailableRolesForRoleAssignment([]);
    }
  };

  // Handle role selection from dropdown in role assignment
  useEffect(() => {
    // STRICT CHECK: Only add role if user explicitly selected it (value changed from previous)
    const roleFilterChanged = prevRoleAssignmentRoleFilter.current !== roleAssignmentRoleFilter;
    const departmentIsSame = prevRoleAssignmentDepartmentFilter.current === roleAssignmentDepartmentFilter;

    // Update refs for next comparison
    prevRoleAssignmentRoleFilter.current = roleAssignmentRoleFilter;
    prevRoleAssignmentDepartmentFilter.current = roleAssignmentDepartmentFilter;

    // Only proceed if:
    // 1. Role filter actually changed (user selected something)
    // 2. Department is the same (not switching departments)
    // 3. New role value is not empty
    if (
      !roleFilterChanged ||
      !departmentIsSame ||
      !roleAssignmentRoleFilter ||
      roleAssignmentRoleFilter === '' ||
      roleAssignmentRoleFilter === 'all' ||
      availableRolesForRoleAssignment.length === 0 ||
      !roleAssignmentDepartmentFilter ||
      roleAssignmentDepartmentFilter === '' ||
      roleAssignmentDepartmentFilter === 'all'
    ) {
      return; // Don't add role
    }

    // Verify the role actually exists in the current department's available roles
    const selectedRole = availableRolesForRoleAssignment.find(r => r.role_key === roleAssignmentRoleFilter);

    // Only proceed if the role exists in the current department
    if (!selectedRole) {
      console.log('Role not found in current department, skipping selection');
      return;
    }

    // Check if this role from this department is already selected
    setSelectedRoles(prev => {
      const roleExists = prev.some(
        r => r.group_key === roleAssignmentDepartmentFilter && r.role_key === roleAssignmentRoleFilter
      );

      if (roleExists) {
        return prev; // Already exists, no change
      }

      // Add the new role - ONLY if it passed all checks above
      console.log('Adding role:', roleAssignmentRoleFilter, 'from department:', roleAssignmentDepartmentFilter);
      return [...prev, {
        group_key: roleAssignmentDepartmentFilter,
        role_key: roleAssignmentRoleFilter,
        role_name: selectedRole.role_name
      }];
    });
  }, [roleAssignmentRoleFilter, roleAssignmentDepartmentFilter, availableRolesForRoleAssignment]);

  // Load certifications using the same API as Certifications.tsx
  const loadCertifications = async (page: number = certificationPagination.page, limit: number = certificationPagination.limit) => {
    try {
      setLoading(true);

      // Build query params - ALWAYS include is_published=true to show only published certifications
      const params: any = {
        page: page,
        limit: limit,
        is_published: true, // REQUIRED: Only show published certifications for assignment
        sortBy: 'updatedAt',
        sortOrder: 'desc'
      };

      // Add search parameter if searchQuery is not empty
      if (searchQuery) {
        params.search = searchQuery;
      }

      // Call the same API used in Certifications.tsx
      const response = await certificationService.getMinimalCertifications(params);

      if (response && response.certifications) {
        // Map the response to match our Certification interface
        const certifications = response.certifications.map((cert: any) => ({
          _id: cert._id,
          id: cert._id,
          certification_id: cert.certification_id || cert._id, // Use certification_id if available, fallback to _id
          title: cert.title,
          description: cert.description,
          difficulty: cert.difficulty || 'N/A',
          question_count: cert.question_count,
          thumbnail_image_url: cert.thumbnail_image_url,
          banner_image_url: cert.banner_image_url,
          passing_score: cert.passing_score,
          time_limit: cert.time_limit_minutes
        }));

        setCertifications(certifications);

        // Update pagination state
        setCertificationPagination(prev => ({
          ...prev,
          page: page,
          limit: limit,
          totalPages: response.pagination?.totalPages || Math.ceil((response.total || certifications.length) / limit),
          totalItems: response.pagination?.totalItems || response.total || certifications.length
        }));
      }
    } catch (error) {
      console.error('Failed to load certifications:', error);
      toast.error('Failed to load certifications');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (page: number = userPagination.page, limit?: number) => {
    try {
      setUsersLoading(true);
      const effectiveLimit = limit ?? userPagination.limit;
      const params: any = {
        status: 'ACTIVE',  // Only load active users
        page: page,
        limit: effectiveLimit
      };

      // Add search parameter if searchTerm is not empty
      if (recipientSearchQuery) {
        params.search = recipientSearchQuery;
      }

      // Add group filter if a department is selected
      if (recipientDepartmentFilterValue !== 'all') {
        params.group_key = recipientDepartmentFilterValue;
      }

      // Add role filter if a role is selected
      if (selectedRoleFilterValue !== 'all') {
        params.role_key = selectedRoleFilterValue;
      }

      const response = await courseAssignmentDataService.loadUsers(params);
      if (response.status === 'success' && response.data) {
        setUsers(response.data.data);

        // Update pagination state
        setUserPagination(prev => ({
          ...prev,
          page: page,
          limit: effectiveLimit,
          totalPages: response.data.pagination?.totalPages || Math.ceil((response.data.total || response.data.data.length) / effectiveLimit),
          totalItems: response.data.pagination?.totalItems || response.data.total || response.data.data.length
        }));
      }
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

const loadGroups = async (page: number = groupPagination.page, limit?: number) => {
  try {
    setGroupsLoading(true);
    const effectiveLimit = limit ?? groupPagination.limit;

    const skip = (page - 1) * effectiveLimit;

    const body: any = {
      skip: skip,
      limit: effectiveLimit
    };

    if (recipientSearchQuery) {
      body.group_name = recipientSearchQuery;
    }

    const response = await courseAssignmentDataService.loadGroups(body);

    if (response.status === 'success' && response.data) {
      const groupsData = response.data.groups || [];
      setGroups(groupsData);

      setGroupPagination(prev => ({
        ...prev,
        page: page,
        totalPages:
          Math.ceil((response.data.total || groupsData.length) / prev.limit),
        totalItems: response.data.total || groupsData.length
      }));
    } else {
      console.warn('Groups response missing data:', response);
      setGroups([]);
    }
  } catch (error) {
    console.error('Error loading groups:', error);
    toast.error('Failed to load groups');
  } finally {
    setGroupsLoading(false);
  }
};

  const loadRoles = async (page: number = rolePagination.page, limit?: number) => {
    try {
      setRolesLoading(true);
      const effectiveLimit = limit ?? rolePagination.limit;
      const skip = (page - 1) * effectiveLimit;
      const params: any = {
        skip: skip,
        limit: effectiveLimit
      };

      // Add search parameter
      if (recipientSearchQuery) {
        params.role_name = recipientSearchQuery;
      }

      const response = await courseAssignmentDataService.loadRoles(params);
      if (response.status === 'success' && response.data) {
        setRoles(response.data.roles);

        // Update pagination state
        setRolePagination(prev => ({
          ...prev,
          page: page,
          limit: effectiveLimit,
          totalPages: response.data.pagination?.totalPages || Math.ceil((response.data.total || response.data.roles.length) / effectiveLimit),
          totalItems: response.data.pagination?.totalItems || response.data.total || response.data.roles.length
        }));
      }
    } catch (error) {
      toast.error('Failed to load roles');
    } finally {
      setRolesLoading(false);
    }
  };

  const loadRecentAssignments = async () => {
    try {
      setRecentAssignmentsLoading(true);

      // Load recent assignments using listAssignments
      const response: any = await certificationAssignmentService.listAssignments({
        limit: 5,
        page: 1
      });

      console.log('Recent assignments response:', response);
      // Service returns { data: [...], total: X, page: "1", limit: "5" }
      // We need the data array
      if (response && response.data && Array.isArray(response.data)) {
        setRecentAssignments(response.data);
      } else {
        setRecentAssignments([]);
      }
    } catch (error) {
      console.error('Failed to load recent assignments:', error);
      setRecentAssignments([]);
    } finally {
      setRecentAssignmentsLoading(false);
    }
  };

  // Deadline handlers
  const handleDeadlineDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setDeadlineDays(undefined);
      setDeadlineError('');
      setAssignmentSettings(prev => ({ ...prev, deadline_days: undefined }));
      return;
    }
    const days = parseInt(value);
    if (isNaN(days) || days < 1) {
      setDeadlineError('Deadline must be a positive number.');
      setDeadlineDays(undefined);
      setAssignmentSettings(prev => ({ ...prev, deadline_days: undefined }));
      return;
    }
    if (days > 365) {
      setDeadlineError('Deadline cannot exceed 365 days.');
      setDeadlineDays(undefined);
      setAssignmentSettings(prev => ({ ...prev, deadline_days: undefined }));
      return;
    }
    setDeadlineError('');
    setDeadlineDays(days);
    setAssignmentSettings(prev => ({ ...prev, deadline_days: days }));
  };

  const handleDeadlineDaysBlur = () => {
    if (deadlineDays !== undefined && deadlineDays < 1) {
      setDeadlineError('Deadline must be at least 1 day');
    }
  };

  // Handle bulk import success
  const handleBulkImportSuccess = () => {
    setShowBulkImportModal(false);
    // Stay on bulk tab after successful import
    loadRecentAssignments(); // Reload assignments list
    toast.success('Bulk import completed successfully!');
  };

  // Assignment action handlers
  const handleSendReminder = async (assignmentId: string) => {
    try {
      setActionLoading(assignmentId);

      const reminderData = await certificationAssignmentService.sendReminderNotifications(assignmentId);

      const { message, failure_count } = reminderData;

      if (failure_count === 0) {
        toast.success(message);
      } else {
        toast.warning(message);
      }

    } catch (error: any) {
      console.error('Failed to send reminders:', error);
      toast.error(error.message || 'Failed to send reminders');
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!assignmentToDelete) return;

    try {
      setActionLoading(assignmentToDelete.id);
      await certificationAssignmentService.deleteAssignment(assignmentToDelete.id);
      toast.success('Assignment deleted successfully!');
      setShowDeleteModal(false);
      setAssignmentToDelete(null);
      loadRecentAssignments(); // Reload the list
    } catch (error: any) {
      console.error('Failed to delete assignment:', error);
      toast.error(error.message || 'Failed to delete assignment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setAssignmentToDelete(null);
  };

  // Selection handlers
  const handleSelectCertification = (certificationId: string) => {
    setSelectedCertifications(prev => {
      if (prev.includes(certificationId)) {
        selectedCertDataRef.current.delete(certificationId);
        return prev.filter(id => id !== certificationId);
      } else {
        const cert = certifications.find(c => c.certification_id === certificationId);
        if (cert) selectedCertDataRef.current.set(certificationId, cert);
        return [...prev, certificationId];
      }
    });
  };

  // Selected users may not be on the current page of results, so fall back to the cache
  const getSelectedUser = (userId: string) =>
    users.find(u => u._id === userId) || selectedUserDataRef.current.get(userId);

  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectDepartment = (deptId: string) => {
    setSelectedDepartments(prev =>
      prev.includes(deptId) ? [] : [deptId]
    );
  };

  const handleSelectRole = (groupKey: string, roleKey: string) => {
    setSelectedRoles(prev =>
      prev.filter(r => !(r.group_key === groupKey && r.role_key === roleKey))
    );
  };

  const handlePreviewClick = async () => {
    // Reset previous duplicate check
    setDuplicateCheckResult(null);

    // Validate selections
    if (!selectedCertifications || selectedCertifications.length === 0) {
      toast.error('Please select at least one certification');
      return;
    }

    const targetIds = assignmentType === 'department' ? selectedDepartments
      : assignmentType === 'role' ? selectedRoles.map(r => ({ group_key: r.group_key, role_key: r.role_key }))
        : selectedUsers;

    if (!targetIds || targetIds.length === 0) {
      toast.error('Please select recipients for the assignment');
      return;
    }

    // Check for duplicates
    setIsCheckingDuplicates(true);
    try {
      const checkType = assignmentType === 'department' ? 'groups'
        : assignmentType === 'role' ? 'roles'
        : 'users';

      // For duplicate check: groups use _id, roles use objects with group_key and role_key
      let targetIdsForCheck = targetIds;
      if (assignmentType === 'department') {
        // For groups, send _id (not group_key)
        targetIdsForCheck = selectedDepartments;
      } else if (assignmentType === 'role') {
        // selectedRoles is already mapped to objects with group_key and role_key
        targetIdsForCheck = targetIds;
      }

      const duplicateCheck = await certificationAssignmentService.checkForDuplicates({
        type: checkType as 'users' | 'groups' | 'roles',
        target_ids: targetIdsForCheck,
        certifications_ids: selectedCertifications
      });

      // Map API response to match the expected format
      if (duplicateCheck.has_conflicts) {
        setDuplicateCheckResult({
          hasConflicts: true,
          conflicts: duplicateCheck.conflicts.map((conflict: any) => ({
            targetId: conflict.target_id,
            targetType: conflict.target_type,
            certificationId: conflict.certification_id,
            conflictType: conflict.conflict_type,
            existingAssignments: conflict.existing_assignments,
            affectedUsers: conflict.affected_users
          })),
          summary: duplicateCheck.summary
        });
      } else {
        setDuplicateCheckResult(null);
      }
    } catch (error: any) {
      console.error('Error checking duplicates:', error);
      // Continue to preview even if duplicate check fails
      toast.error('Unable to check for duplicates. Proceeding with assignment.');
      setDuplicateCheckResult(null);
    } finally {
      setIsCheckingDuplicates(false);
    }

    // Always show preview modal regardless of conflicts
    setShowPreview(true);
  };

  const handleAssignCertifications = async () => {
    if (!selectedCertifications || selectedCertifications.length === 0) {
      toast.error('Please select at least one certification');
      return;
    }

    const targetIds = assignmentType === 'department' ? selectedDepartments
      : assignmentType === 'role' ? selectedRoles.map(r => ({ group_key: r.group_key, role_key: r.role_key }))
        : selectedUsers;

    if (!targetIds || targetIds.length === 0) {
      toast.error('Please select recipients for the assignment');
      return;
    }

    // Runs the shared Textarea's clean-text rule; the field shows its own error
    if (!(await trigger('assignmentMessage'))) {
      toast.error('Please fix the message before assigning');
      return;
    }

    setIsAssigning(true);
    try {
      // When skipDuplicates is enabled, filter out users/groups/roles with conflicts
      let usersToAssign = selectedUsers;
      let departmentsToAssign = selectedDepartments;
      let rolesToAssign = selectedRoles;
      let skippedTargetsCount = 0;

      if (skipDuplicates && duplicateCheckResult?.hasConflicts) {
        if (assignmentType === 'role') {
          // For role assignments, filter out roles that have conflicts with ALL selected certifications
          // Build a map of role -> certifications with conflicts
          const roleConflictsMap = new Map<string, Set<string>>();

          duplicateCheckResult.conflicts.forEach((conflict: any) => {
            const roleId = conflict.targetId; // Format: "group_key_role_key"
            if (!roleConflictsMap.has(roleId)) {
              roleConflictsMap.set(roleId, new Set());
            }
            roleConflictsMap.get(roleId)!.add(conflict.certificationId);
          });

          // Filter out roles that have conflicts with ALL selected certifications
          rolesToAssign = selectedRoles.filter(roleObj => {
            const roleId = `${roleObj.group_key}_${roleObj.role_key}`;
            const conflictingCertifications = roleConflictsMap.get(roleId);

            // If this role has no conflicts, include it
            if (!conflictingCertifications) {
              return true;
            }

            // If this role has conflicts with ALL selected certifications, exclude it
            const hasAllConflicts = selectedCertifications.every(certificationId => conflictingCertifications.has(certificationId));
            return !hasAllConflicts;
          });

          if (rolesToAssign.length === 0) {
            toast.warning('All selected certifications are already assigned to all selected roles. No new assignments to create.');
            setIsAssigning(false);
            return;
          }

          console.log('[Assignment] Filtered roles:', {
            original: selectedRoles.length,
            filtered: rolesToAssign.length,
            excluded: selectedRoles.length - rolesToAssign.length
          });
        } else if (assignmentType === 'department') {
          // For department assignments, filter out departments that have conflicts with ALL selected certifications
          // Build a map of department -> certifications with conflicts
          const deptConflictsMap = new Map<string, Set<string>>();

          duplicateCheckResult.conflicts.forEach((conflict: any) => {
            const deptId = conflict.targetId;
            if (!deptConflictsMap.has(deptId)) {
              deptConflictsMap.set(deptId, new Set());
            }
            deptConflictsMap.get(deptId)!.add(conflict.certificationId);
          });

          // Filter out departments that have conflicts with ALL selected certifications
          departmentsToAssign = selectedDepartments.filter(deptId => {
            const conflictingCertifications = deptConflictsMap.get(deptId);

            // If this department has no conflicts, include it
            if (!conflictingCertifications) {
              return true;
            }

            // If this department has conflicts with ALL selected certifications, exclude it
            const hasAllConflicts = selectedCertifications.every(certificationId => conflictingCertifications.has(certificationId));
            return !hasAllConflicts;
          });

          if (departmentsToAssign.length === 0) {
            toast.warning('All selected certifications are already assigned to all selected departments. No new assignments to create.');
            setIsAssigning(false);
            return;
          }

          console.log('[Assignment] Filtered departments:', {
            original: selectedDepartments.length,
            filtered: departmentsToAssign.length,
            excluded: selectedDepartments.length - departmentsToAssign.length
          });
        } else {
          // For user assignments, filter out users that have conflicts with ALL selected certifications
          // Build a map of user -> certifications with conflicts
          const userConflictsMap = new Map<string, Set<string>>();

          duplicateCheckResult.conflicts.forEach((conflict: any) => {
            const userId = conflict.targetId;
            if (!userConflictsMap.has(userId)) {
              userConflictsMap.set(userId, new Set());
            }
            userConflictsMap.get(userId)!.add(conflict.certificationId);
          });

          // Filter out users that have conflicts with ALL selected certifications
          usersToAssign = selectedUsers.filter(userId => {
            const conflictingCertifications = userConflictsMap.get(userId);

            // If this user has no conflicts, include them
            if (!conflictingCertifications) {
              return true;
            }

            // If this user has conflicts with ALL selected certifications, exclude them
            const hasAllConflicts = selectedCertifications.every(certId => conflictingCertifications.has(certId));
            return !hasAllConflicts;
          });

          if (usersToAssign.length === 0) {
            toast.warning('All selected certifications are already assigned to all selected users. No new assignments to create.');
            setIsAssigning(false);
            return;
          }

          skippedTargetsCount = selectedUsers.length - usersToAssign.length;

          console.log('[Assignment] Filtered users:', {
            original: selectedUsers.length,
            filtered: usersToAssign.length,
            excluded: skippedTargetsCount
          });
        }
      }

      // Prepare assignment data - map UI fields to API fields
      const assignmentData: any = {
        certification_ids: selectedCertifications,
        is_mandatory: Boolean(assignmentSettings.is_mandatory),
        send_notification: Boolean(assignmentSettings.send_notification),
        // Only send reminder fields if notification is enabled
        enable_automatic_reminder: assignmentSettings.send_notification ? assignmentSettings.enable_automatic_reminder : false,
        skip_duplicates: skipDuplicates // Let backend handle per-user/group/role duplicate detection
      };

      // Only include reminder_frequency_days if both notification and automatic reminder are enabled
      if (assignmentSettings.send_notification && assignmentSettings.enable_automatic_reminder) {
        assignmentData.reminder_frequency_days = assignmentSettings.reminder_frequency_days > 0 ? assignmentSettings.reminder_frequency_days : 7;
      }

      // Only include deadline_days if user has entered a value
      if (deadlineDays !== undefined && deadlineDays > 0) {
        assignmentData.deadline_days = deadlineDays;
      }

      // Only include message if user has entered one
      const assignmentMessage = getValues('assignmentMessage');
      if (assignmentMessage) {
        assignmentData.message = cleanText(assignmentMessage);
      }

      console.log('[Assignment] assignmentData with message:', JSON.stringify(assignmentData, null, 2));
      console.log('[Assignment] assignmentMessage:', assignmentMessage);

      let response;

      // Call appropriate API based on assignment type
      if (assignmentType === 'individual') {
        const requestData: any = {
          ...assignmentData,
          user_ids: usersToAssign
        };

        // Add skipped_users to payload if there are conflicts
        if (skipDuplicates && duplicateCheckResult?.hasConflicts) {
          const skippedUsers = duplicateCheckResult.conflicts.map((conflict: any) => {
            const user = getSelectedUser(conflict.targetId);
            return {
              user_id: conflict.targetId,
              user_email: user?.email || 'Unknown',
              user_name: user?.full_name || user?.given_name || 'Unknown User',
              certification_id: conflict.certificationId,
              reason: 'User already has this certification assigned'
            };
          });
          if (skippedUsers.length > 0) {
            requestData.skipped_users = skippedUsers;
          }
        }

        response = await certificationAssignmentService.assignToUsers(requestData);
      } else if (assignmentType === 'department') {
        // Map selected department IDs to group_keys
        const groupKeys = departmentsToAssign.map(deptId => {
          const group = groups.find(g => g._id === deptId);
          return group?.group_key;
        }).filter(Boolean) as string[];

        const requestData: any = {
          ...assignmentData,
          group_keys: groupKeys
        };

        // Add skipped_groups to payload if there are conflicts
        if (skipDuplicates && duplicateCheckResult?.hasConflicts) {
          const skippedGroups = duplicateCheckResult.conflicts.map((conflict: any) => {
            const group = groups.find(g => g._id === conflict.targetId);
            return {
              group_key: group?.group_key || conflict.targetId,
              group_name: group?.group_name || 'Unknown Group',
              certification_id: conflict.certificationId,
              reason: 'Group already has this certification assigned'
            };
          });
          if (skippedGroups.length > 0) {
            requestData.skipped_groups = skippedGroups;
          }
        }

        console.log('[Assignment] Group requestData:', JSON.stringify(requestData, null, 2));
        response = await certificationAssignmentService.assignToGroups(requestData);
      } else if (assignmentType === 'role') {
        // For roles, send array of objects with group_key and role_key
        // filteredRolesToAssign is already an array of { group_key, role_key, role_name? }
        const roleAssignments = rolesToAssign.map(r => ({
          group_key: r.group_key,
          role_key: r.role_key
        }));

        const requestData: any = {
          ...assignmentData,
          role_keys: roleAssignments
        };

        // Add skipped_roles to payload if there are conflicts
        if (skipDuplicates && duplicateCheckResult?.hasConflicts) {
          const skippedRoles = duplicateCheckResult.conflicts.map((conflict: any) => {
            // targetId format is "group_key_role_key"
            const [groupKey, ...roleKeyParts] = conflict.targetId.split('_');
            const roleKey = roleKeyParts.join('_');
            const roleObj = selectedRoles.find(r => r.group_key === groupKey && r.role_key === roleKey);
            return {
              group_key: groupKey,
              role_key: roleKey,
              role_name: roleObj?.role_name || roleKey,
              certification_id: conflict.certificationId,
              reason: 'Role already has this certification assigned'
            };
          });
          if (skippedRoles.length > 0) {
            requestData.skipped_roles = skippedRoles;
          }
        }

        console.log('[Assignment] Role requestData:', JSON.stringify(requestData, null, 2));
        response = await certificationAssignmentService.assignToRoles(requestData);
      }

      // Show success message with details
      if (response) {
        console.log('Assignment API Response:', response);
        console.log('Assignment data sent:', { usersToAssign, departmentsToAssign, rolesToAssign });

        // Check if API returned a message
        if (response.message) {
          // Use the message from the API response
          toast.success(response.message);
        } else {
          // Fallback to constructing the message
          // Calculate how many recipients we actually sent to the API
          let recipientCount = 0;
          if (assignmentType === 'individual') {
            recipientCount = usersToAssign.length;
          } else if (assignmentType === 'department') {
            recipientCount = departmentsToAssign.length;
          } else if (assignmentType === 'role') {
            recipientCount = rolesToAssign.length;
          }

          // Get counts from API response with fallbacks
          const successfulCount = response.successful_assignments ?? response.successful_assignments_count ?? response.successful ?? response.total_users_assigned ?? recipientCount;
          const failedCount = response.failed_assignments ?? response.failed_assignments_count ?? response.failed ?? 0;
          const totalCount = response.total_assignments ?? response.total_assignments_count ?? response.total ?? recipientCount;

          console.log('Assignment counts:', {
            recipientCount,
            successfulCount,
            failedCount,
            totalCount,
            responseKeys: Object.keys(response)
          });

          // Build success message
          let successMessage = '';
          if (failedCount > 0 && successfulCount > 0) {
            successMessage = `${successfulCount} of ${totalCount} assignments succeeded. ${failedCount} failed.`;
          } else if (failedCount > 0) {
            successMessage = `Failed to assign certifications. ${failedCount} assignments failed.`;
          } else {
            // Use recipientCount as the most reliable count (we know how many we sent)
            const displayCount = successfulCount || recipientCount;
            successMessage = `Successfully assigned certifications to ${displayCount} recipient(s)!`;
          }

          // Add information about skipped items if we filtered them on frontend
          if (skipDuplicates && duplicateCheckResult?.hasConflicts) {
            if (assignmentType === 'role') {
              // For roles, show how many roles were excluded
              const excludedRolesCount = selectedRoles.length - rolesToAssign.length;
              if (excludedRolesCount > 0) {
                successMessage += ` (${excludedRolesCount} role${excludedRolesCount > 1 ? 's' : ''} with complete conflicts excluded)`;
              }
            } else if (assignmentType === 'department') {
              // For departments, show how many departments were excluded
              const excludedDeptsCount = selectedDepartments.length - departmentsToAssign.length;
              if (excludedDeptsCount > 0) {
                successMessage += ` (${excludedDeptsCount} department${excludedDeptsCount > 1 ? 's' : ''} with complete conflicts excluded)`;
              }
            } else {
              // For users, show how many users were excluded
              const excludedUsersCount = selectedUsers.length - usersToAssign.length;
              if (excludedUsersCount > 0) {
                successMessage += ` (${excludedUsersCount} user${excludedUsersCount > 1 ? 's' : ''} with complete conflicts excluded)`;
              }
            }
          }

          if (failedCount > 0) {
            toast.error(successMessage);
          } else {
            toast.success(successMessage);
          }
        }

        // Handle skipped items from backend response (conflicts/duplicates)
        if (response.skipped_users && response.skipped_users.length > 0) {
          const skippedCount = response.skipped_users.length;
          toast.warning(`${skippedCount} user${skippedCount > 1 ? 's' : ''} skipped due to existing assignments`);
        }
        if (response.skipped_groups && response.skipped_groups.length > 0) {
          const skippedCount = response.skipped_groups.length;
          toast.warning(`${skippedCount} group${skippedCount > 1 ? 's' : ''} skipped due to existing assignments`);
        }
        if (response.skipped_roles && response.skipped_roles.length > 0) {
          const skippedCount = response.skipped_roles.length;
          toast.warning(`${skippedCount} role${skippedCount > 1 ? 's' : ''} skipped due to existing assignments`);
        }
      } else {
        toast.success('Certifications assigned successfully!');
      }

      // Reset form state and close popup
      resetFormState();
      setShowPreview(false);

      // Reload recent assignments
      loadRecentAssignments();
    } catch (error: any) {
      console.error('Assignment error:', error);

      // Extract error message from API response structure
      let errorMessage = 'Failed to assign certifications';

      if (error?.response?.data?.error?.error_description) {
        errorMessage = error.response.data.error.error_description;
      } else if (error?.response?.data?.description) {
        errorMessage = error.response.data.description;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsAssigning(false);
    }
  };

  // Reset assignment form state
  const resetFormState = useCallback(() => {
    setSelectedCertifications([]);
    selectedCertDataRef.current.clear();
    setSelectedUsers([]);
    selectedUserDataRef.current.clear();
    setSelectedDepartments([]);
    setSelectedRoles([]);
    setDeadlineDays(undefined);
    setDeadlineError('');
    setAssignmentSettings({
      is_mandatory: false,
      send_notification: true,
      enable_automatic_reminder: true,
      reminder_frequency_days: 7,
      deadline_days: undefined,
      message: undefined
    });
    setValue('assignmentMessage', '');
    // Reset role assignment form fields
    setValue('roleAssignmentDepartmentFilter', '');
    setValue('roleAssignmentRoleFilter', '');
    // Reset refs to prevent auto-selection on next use
    prevRoleAssignmentRoleFilter.current = '';
    prevRoleAssignmentDepartmentFilter.current = '';
    // Clear available roles for role assignment
    setAvailableRolesForRoleAssignment([]);
  }, [setValue]);

  // Pagination handlers
  const handleCertificationPageChange = (page: number) => {
    setCertificationPagination(prev => ({ ...prev, page }));
    loadCertifications(page);
  };

  const handleCertificationLimitChange = (limit: number) => {
    setCertificationPagination(prev => ({ ...prev, limit, page: 1 }));
    loadCertifications(1, limit);
  };

  const handleUserPageChange = (page: number) => {
    setUserPagination(prev => ({ ...prev, page }));
    loadUsers(page);
  };

  const handleUserLimitChange = (limit: number) => {
    setUserPagination(prev => ({ ...prev, limit, page: 1 }));
    loadUsers(1, limit);
  };

  const handleGroupPageChange = (page: number) => {
    setGroupPagination(prev => ({ ...prev, page }));
    loadGroups(page);
  };

  const handleGroupLimitChange = (limit: number) => {
    setGroupPagination(prev => ({ ...prev, limit, page: 1 }));
    loadGroups(1, limit);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-amber-600 to-brand-orange-600 rounded-xl p-4 lg:p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-xl lg:text-2xl font-bold mb-2">Certification Assignment</h1>
            <p className="text-brand-amber-100 text-sm lg:text-base">
              Assign certifications to individuals, teams, or entire departments
            </p>
          </div>
          <div className="flex gap-2 lg:gap-3">
            <Link
              to={canViewAnalytics ? ADMIN_CERTIFICATION_ASSIGNMENT_ANALYTICS : "#"}
              onClick={(e) => !canViewAnalytics && e.preventDefault()}
              className={`flex items-center gap-2 px-3 lg:px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg transition-colors text-sm ${
                canViewAnalytics ? 'hover:bg-white/30' : 'opacity-50 cursor-not-allowed'
              }`}
              title={!canViewAnalytics ? 'You do not have permission to view analytics' : undefined}
            >
              <TrendingUp className="w-4 lg:w-5 h-4 lg:h-5" />
              <span className="hidden sm:inline">View Analytics</span>
              <span className="sm:hidden">Analytics</span>
            </Link>
          </div>
        </div>

        {/* Assignment Type Selector */}
        <div className="flex flex-wrap gap-2 mt-6">
          <button
            onClick={() => setAssignmentType('individual')}
            className={`px-3 lg:px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm ${
              assignmentType === 'individual'
                ? 'bg-white text-brand-amber-600'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            <UserIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Individual</span>
            <span className="sm:hidden">Users</span>
          </button>
          <button
            onClick={() => setAssignmentType('department')}
            className={`px-3 lg:px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm ${
              assignmentType === 'department'
                ? 'bg-white text-brand-amber-600'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            <Building className="w-4 h-4" />
            <span className="hidden sm:inline">Department</span>
            <span className="sm:hidden">Dept</span>
          </button>
          <button
            onClick={() => setAssignmentType('role')}
            className={`px-3 lg:px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm ${
              assignmentType === 'role'
                ? 'bg-white text-brand-amber-600'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            <Briefcase className="w-4 h-4" />
            <span className="hidden sm:inline">Role-Based</span>
            <span className="sm:hidden">Roles</span>
          </button>
          <button
            onClick={() => setAssignmentType('bulk')}
            className={`px-3 lg:px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm ${
              assignmentType === 'bulk'
                ? 'bg-white text-brand-amber-600'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Bulk Import</span>
            <span className="sm:hidden">Bulk</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6">
        {/* Certification Selection */}
        <div className="space-y-4">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-gray-900">Select Certifications</h2>
                <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full border border-green-300">
                  🟢 Published Only
                </span>
              </div>
              <span className="text-sm text-gray-500">
                {selectedCertifications?.length || 0} selected
              </span>
            </div>

            {/* Filters */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search certifications..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-amber-500"
                />
              </div>
            </div>

            {/* Certification List */}
            <div className="space-y-3 max-h-[753px] overflow-y-auto">
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="p-3 border border-gray-200 rounded-lg animate-pulse">
                      <div className="flex items-start gap-3">
                        <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : certifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 bg-brand-amber-100 rounded-full flex items-center justify-center mb-4">
                    <Medal className="w-8 h-8 text-brand-amber-600" />
                  </div>
                  <p className="text-gray-900 font-medium mb-1">No certifications available</p>
                  <p className="text-sm text-gray-500">Create a certification to get started</p>
                  <Link
                    to="/admin/certification/create"
                    className="mt-4 px-4 py-2 bg-brand-amber-600 text-white rounded-lg hover:bg-brand-amber-700 transition-colors text-sm"
                  >
                    Create Certification
                  </Link>
                </div>
              ) : (
                certifications.map((certification) => {
                  // Get thumbnail URL with fallback to banner or default icon
                  const thumbnailUrl = certification.thumbnail_image_url || certification.banner_image_url;

                  return (
                  <label
                    key={certification._id}
                    className="block p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedCertifications.includes(certification.certification_id)}
                        onChange={() => handleSelectCertification(certification.certification_id)}
                        className="mt-1 rounded border-gray-300"
                      />
                      {thumbnailUrl ? (
                        <img
                          src={thumbnailUrl}
                          alt={certification.title}
                          className="w-16 h-16 rounded-lg object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <div className={`w-16 h-16 bg-gradient-to-br from-brand-amber-500 to-brand-orange-600 rounded-lg flex items-center justify-center text-white ${thumbnailUrl ? 'hidden' : ''}`}>
                        <Award className="w-8 h-8" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 mr-4">
                            <p className="font-medium text-gray-900">{certification.title}</p>
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{certification.description}</p>
                            <div className="flex items-center gap-3 mt-2 flex-wrap">
                              <span className="text-xs text-gray-500 flex items-center">
                                <Clock className="w-3 h-3 mr-1" />
                                {certification.time_limit ? `${certification.time_limit} min` : 'No limit'}
                              </span>
                              <span className="text-xs text-gray-500 flex items-center">
                                <Clipboard className="w-3 h-3 mr-1" />
                                {certification?.question_count ? `${certification.question_count} Questions` : 'N/A'}
                              </span>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-xs text-gray-600">
                              <span className="font-medium">{certification.question_count || 0}</span> questions
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              Pass: {certification.passing_score || 70}%
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </label>
                  );
                })
              )}
            </div>

            {/* Certification Pagination */}
            {certifications.length > 0 && (
              <Pagination
                currentPage={certificationPagination.page}
                totalPages={certificationPagination.totalPages}
                totalItems={certificationPagination.totalItems}
                itemsPerPage={certificationPagination.limit}
                onPageChange={handleCertificationPageChange}
                onItemsPerPageChange={handleCertificationLimitChange}
                showItemsPerPage={true}
                itemsPerPageOptions={[10, 20, 50]}
              />
            )}
          </div>
        </div>

        {/* Recipients Selection */}
        <div className="space-y-4">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Select {assignmentType === 'department' ? 'Departments' : assignmentType === 'role' ? 'Roles' : 'Recipients'}
              </h2>
              <span className="text-sm text-gray-500">
                {assignmentType === 'department' ? selectedDepartments?.length || 0 : assignmentType === 'role' ? selectedRoles?.length || 0 : selectedUsers?.length || 0} selected
              </span>
            </div>

            {/* Recipient Filters */}
            {assignmentType !== 'bulk' && (
              <div className="space-y-3 mb-4">
                {/* Search Input - Full Width (not for role assignment) */}
                {assignmentType !== 'role' && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder={`Search ${
                        assignmentType === 'department'
                          ? 'departments by name'
                          : 'users by name, email, or employee ID'
                      }...`}
                      value={recipientSearchQuery}
                      onChange={(e) => setRecipientSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-amber-500"
                    />
                  </div>
                )}

                {/* Department and Role Filters - Below Search */}
                {assignmentType === 'individual' && (
                  <div className="flex gap-3">
                    <Select<FilterFormData>
                      control={control}
                      name="recipientDepartmentFilter"
                      options={[
                        { value: 'all', label: 'All Departments' },
                        ...availableDepartments.map(dept => ({
                          value: dept.group_key || dept._id,
                          label: dept.group_name
                        }))
                      ]}
                      placeholder="Select Department"
                      isClearable={false}
                      isSearchable={true}
                    />
                    {recipientDepartmentFilterValue !== 'all' && availableRolesForDepartment.length > 0 && (
                      <Select<FilterFormData>
                        control={control}
                        name="selectedRoleFilter"
                        options={[
                          { value: 'all', label: 'All Roles' },
                          ...availableRolesForDepartment.map(role => ({
                            value: role.role_key,
                            label: role.role_name
                          }))
                        ]}
                        placeholder="Select Role"
                        isClearable={false}
                        isSearchable={true}
                      />
                    )}
                  </div>
                )}

                {/* Department and Role Filters for Role Assignment */}
                {assignmentType === 'role' && (
                  <div className="flex gap-3">
                    <Select<FilterFormData>
                      control={control}
                      name="roleAssignmentDepartmentFilter"
                      options={[
                        { value: '', label: 'Select Department' },
                        ...availableDepartments.map(dept => ({
                          value: dept.group_key,
                          label: dept.group_name
                        }))
                      ]}
                      placeholder="Select Department"
                      isClearable={false}
                    />
                    {roleAssignmentDepartmentFilter && roleAssignmentDepartmentFilter !== '' && availableRolesForRoleAssignment.length > 0 && (
                      <Select<FilterFormData>
                        key={`role-select-${roleAssignmentDepartmentFilter}`}
                        control={control}
                        name="roleAssignmentRoleFilter"
                        options={[
                          { value: '', label: 'Select Role' },
                          ...availableRolesForRoleAssignment.map(role => ({
                            value: role.role_key,
                            label: role.role_name
                          }))
                        ]}
                        placeholder="Select Role"
                        isClearable={false}
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            {assignmentType === 'bulk' ? (
              <div className="space-y-6">
                {/* CSV Bulk Import Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">📤 CSV Bulk Import</h3>
                  <p className="text-sm text-blue-800 mb-3">
                    Import multiple certification assignments at once using a CSV file. You can assign certifications to users (by email), groups, or roles.
                  </p>
                  <button
                    onClick={() => setShowBulkImportModal(true)}
                    disabled={!canWrite}
                    title={!canWrite ? 'You do not have permission to perform this action' : undefined}
                    className="flex items-center gap-2 px-4 py-2 bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Upload className="w-4 h-4" />
                    Open Bulk Import
                  </button>
                </div>

                {/* Quick Tips */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Quick Tips:</h4>
                  <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                    <li>Use email addresses for USER assignments</li>
                    <li>Use group keys for GROUP assignments (e.g., engineering, sales)</li>
                    <li>Use format <strong>group_key(role_key)</strong> for ROLE assignments (e.g., engineering(manager), sales(admin))</li>
                    <li>Deadline is specified as number of days from import date (e.g., 30, 60, 90)</li>
                    <li>You can mix different assignment types in a single CSV</li>
                    <li>The system will validate all entries before importing</li>
                  </ul>
                </div>

                {/* Sample Format */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Sample CSV Format:</h4>
                  <div className="bg-gray-50 p-3 rounded text-xs font-mono overflow-x-auto">
                    <div>type,identifier,certification_slug,deadline_days,is_mandatory,message,send_email_notification,enable_automatic_reminder,reminder_frequency_days</div>
                    <div>USER,john@example.com,aws-solutions-architect,30,true,Please complete,true,true,7</div>
                    <div>GROUP,engineering,kubernetes-administrator,60,true,,true,true,14</div>
                    <div>ROLE,engineering(manager),project-management,90,true,Important!,true,false,</div>
                  </div>
                </div>
              </div>
            ) : assignmentType === 'department' ? (
              <div className="space-y-4">
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {groupsLoading ? (
                    <div className="space-y-3">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="p-4 border border-gray-200 rounded-lg animate-pulse">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                            <div className="flex-1 space-y-2">
                              <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                              <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : groups.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-16 h-16 bg-brand-purple-100 rounded-full flex items-center justify-center mb-4">
                        <Building className="w-8 h-8 text-brand-purple-600" />
                      </div>
                      <p className="text-gray-900 font-medium mb-1">No departments found</p>
                      <p className="text-sm text-gray-500">Try adjusting your search criteria</p>
                    </div>
                  ) : (
                    groups.map((group) => (
                      <label
                        key={group.group_key || group._id}
                        className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="department-select"
                          checked={selectedDepartments.includes(group._id)}
                          onChange={() => handleSelectDepartment(group._id)}
                          className="border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <div className="w-10 h-10 bg-brand-purple-100 rounded-lg flex items-center justify-center">
                          <Building className="w-5 h-5 text-brand-purple-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{group.group_name}</p>
                          <div className="flex items-center gap-4 mt-1">
                            <span className="text-xs text-gray-500">
                              Key: {group.group_key}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-1">{group.group_description}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">
                            {group.parent_group_key === 'root' ? (
                              <span className="text-brand-blue-600">Root Group</span>
                            ) : (
                              <span>Sub-group</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            Created {new Date(group.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>

                {/* Department Pagination */}
                {groups.length > 0 && (
                  <Pagination
                    currentPage={groupPagination.page}
                    totalPages={groupPagination.totalPages}
                    totalItems={groupPagination.totalItems}
                    itemsPerPage={groupPagination.limit}
                    onPageChange={handleGroupPageChange}
                    onItemsPerPageChange={handleGroupLimitChange}
                    showItemsPerPage={true}
                    itemsPerPageOptions={[10, 20, 50]}
                  />
                )}
              </div>
            ) : assignmentType === 'role' ? (
              <div className="space-y-4">
                {/* Selected Roles Display */}
                {selectedRoles.length > 0 && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-gray-700">Selected Roles</h3>
                      <span className="text-xs text-gray-500">{selectedRoles.length} role{selectedRoles.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedRoles.map((role) => {
                        const dept = availableDepartments.find(d => d.group_key === role.group_key);
                        return (
                          <div
                            key={`${role.group_key}_${role.role_key}`}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-full text-sm"
                          >
                            <Briefcase className="w-3.5 h-3.5 text-brand-green-600" />
                            <span className="text-gray-900 font-medium">{role.role_name}</span>
                            <span className="text-gray-400">·</span>
                            <span className="text-gray-500 text-xs">{dept?.group_name || role.group_key}</span>
                            <button
                              onClick={() => handleSelectRole(role.group_key, role.role_key)}
                              className="ml-1 text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
                      <span className="text-white text-xs font-bold">i</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-blue-900 font-medium mb-1">How to select roles:</p>
                      <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                        <li>Select a department from the dropdown above</li>
                        <li>Choose a role from that department</li>
                        <li>The role will be added to your selection</li>
                        <li>Repeat to add roles from other departments</li>
                        <li>Click the X button on any role chip to remove it</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {usersLoading ? (
                    <div className="space-y-3">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="p-4 border border-gray-200 rounded-lg animate-pulse">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                            <div className="flex-1 space-y-2">
                              <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : users.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-16 h-16 bg-brand-blue-100 rounded-full flex items-center justify-center mb-4">
                        <UserIcon className="w-8 h-8 text-brand-blue-600" />
                      </div>
                      <p className="text-gray-900 font-medium mb-1">No users found</p>
                      <p className="text-sm text-gray-500">Try adjusting your search or filter criteria</p>
                    </div>
                  ) : (
                    users.map((user) => (
                      <label
                        key={user._id}
                        className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user._id)}
                          onChange={() => handleSelectUser(user._id)}
                          className="rounded border-gray-300"
                        />
                        <div className="w-10 h-10 bg-gradient-to-r from-brand-blue-500 to-brand-indigo-500 rounded-full flex items-center justify-center text-white font-semibold">
                          {user.given_name?.[0] || 'U'}{user.family_name?.[0] || ''}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{user.given_name || ''} {user.family_name || ''}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {user.employee_id && (
                              <p className="text-xs text-gray-500">{user.employee_id}</p>
                            )}
                            {(() => {
                              // Show departments/groups
                              const userGroups = user.groups || [];

                              if (userGroups.length === 0) return null;

                              if (userGroups.length === 1) {
                                return (
                                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                                    {userGroups[0].group_name}
                                  </span>
                                );
                              }

                              return (
                                <>
                                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                                    {userGroups[0].group_name}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setSelectedUserDepartments({
                                        userName: `${user.given_name || ''} ${user.family_name || ''}`,
                                        email: user.email,
                                        groups: userGroups
                                      });
                                    }}
                                    className="text-xs text-brand-blue-600 font-medium hover:text-brand-blue-800 hover:underline"
                                  >
                                    +{userGroups.length - 1} more
                                  </button>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">
                            <div>Assigned: {user.certifications?.assigned || 0}</div>
                            <div>Completed: {user.certifications?.completed || 0}</div>
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>

                {/* User Pagination */}
                {users.length > 0 && (
                  <Pagination
                    currentPage={userPagination.page}
                    totalPages={userPagination.totalPages}
                    totalItems={userPagination.totalItems}
                    itemsPerPage={userPagination.limit}
                    onPageChange={handleUserPageChange}
                    onItemsPerPageChange={handleUserLimitChange}
                    showItemsPerPage={true}
                    itemsPerPageOptions={[10, 20, 50]}
                  />
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {assignmentType !== 'bulk' && (
            <div className="card bg-gradient-to-br from-brand-amber-50 to-brand-orange-50">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-brand-amber-600" />
                  <h3 className="font-semibold text-gray-900">Ready to Assign?</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  {selectedCertifications?.length || 0} certification(s) will be assigned to{' '}
                  {assignmentType === 'department'
                    ? `${selectedDepartments?.length || 0} department(s)`
                    : assignmentType === 'role'
                      ? `${selectedRoles?.length || 0} role(s)`
                      : `${selectedUsers?.length || 0} user(s)`}
                </p>
                <button
                  onClick={handlePreviewClick}
                  disabled={!canWrite || isCheckingDuplicates || (selectedCertifications?.length || 0) === 0 || (assignmentType === 'department' ? (selectedDepartments?.length || 0) === 0 : assignmentType === 'role' ? (selectedRoles?.length || 0) === 0 : (selectedUsers?.length || 0) === 0)}
                  title={!canWrite ? 'You do not have permission to perform this action' : undefined}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand-amber-600 text-white rounded-lg hover:bg-brand-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isCheckingDuplicates ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4" />
                      Preview Assignment
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Assignments */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Recent Assignments</h2>
          <Link
            to={canViewAnalytics ? ADMIN_CERTIFICATION_ASSIGNMENT_ANALYTICS : "#"}
            onClick={(e) => !canViewAnalytics && e.preventDefault()}
            className={`text-sm ${
              canViewAnalytics ? 'text-brand-amber-600 hover:text-brand-amber-700' : 'text-brand-amber-600/50 cursor-not-allowed'
            }`}
            title={!canViewAnalytics ? 'You do not have permission to view analytics' : undefined}
          >
            View All →
          </Link>
        </div>

        {recentAssignmentsLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-4 border border-gray-200 rounded-lg animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : !Array.isArray(recentAssignments) || recentAssignments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Medal className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-900 font-medium mb-1">No recent assignments</p>
            <p className="text-sm text-gray-500">Assign certifications to see them here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Users</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Certifications</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Skipped Users</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mandatory</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned By</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deadline Days Count</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(Array.isArray(recentAssignments) ? recentAssignments : []).map((assignment) => (
                  <tr key={assignment._id} className="hover:bg-gray-50">
                    {/* Assigned Users */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2">
                        {/* Display Users */}
                        {assignment.assigned_to_details?.users?.slice(0, 1).map((user: any) => (
                          <div key={user._id} className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-r from-brand-indigo-500 to-brand-purple-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                              {user.name ? user.name.split(' ').map((n: string) => n[0]).join('') : '?'}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{user.name || 'Unknown User'}</p>
                              <p className="text-xs text-gray-500">{user.email || 'N/A'}</p>
                            </div>
                          </div>
                        ))}
                        {assignment.assigned_to_details?.users && assignment.assigned_to_details.users.length > 1 && (
                          <button
                            onClick={() => setSelectedAssignedUsersModal({
                              users: assignment.assigned_to_details.users,
                              type: 'USER'
                            })}
                            className="text-xs text-brand-indigo-600 font-medium hover:text-brand-indigo-800 hover:underline ml-10"
                          >
                            +{assignment.assigned_to_details.users.length - 1} more users
                          </button>
                        )}

                        {/* Display Groups */}
                        {assignment.assigned_to_details?.groups?.slice(0, 1).map((group: any) => (
                          <div key={group._id || group.group_key} className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-r from-brand-blue-500 to-brand-cyan-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                              <Users className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Group: {group.name || group.group_name || 'Unknown Group'}</p>
                              <p className="text-xs text-gray-500">{group.group_key || 'N/A'}</p>
                            </div>
                          </div>
                        ))}
                        {assignment.assigned_to_details?.groups && assignment.assigned_to_details.groups.length > 1 && (
                          <button
                            onClick={() => setSelectedAssignedUsersModal({
                              groups: assignment.assigned_to_details.groups,
                              type: 'GROUP'
                            })}
                            className="text-xs text-brand-indigo-600 font-medium hover:text-brand-indigo-800 hover:underline ml-10"
                          >
                            +{assignment.assigned_to_details.groups.length - 1} more groups
                          </button>
                        )}

                        {/* Display Roles */}
                        {assignment.assigned_to_details?.roles?.slice(0, 1).map((role: any) => (
                          <div key={role._id || role.role_key} className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-r from-brand-amber-500 to-brand-orange-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                              <Shield className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Role: {role.name || role.role_name || 'Unknown Role'}</p>
                              <p className="text-xs text-gray-500">{role.role_key || 'N/A'}</p>
                            </div>
                          </div>
                        ))}
                        {assignment.assigned_to_details?.roles && assignment.assigned_to_details.roles.length > 1 && (
                          <button
                            onClick={() => setSelectedAssignedUsersModal({
                              roles: assignment.assigned_to_details.roles,
                              type: 'ROLE'
                            })}
                            className="text-xs text-brand-indigo-600 font-medium hover:text-brand-indigo-800 hover:underline ml-10"
                          >
                            +{assignment.assigned_to_details.roles.length - 1} more roles
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Certifications */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="text-sm text-gray-900 truncate max-w-[200px]">
                          {assignment.certifications?.[0]?.name || 'N/A'}
                        </div>
                        {assignment.certifications && assignment.certifications.length > 1 && (
                          <button
                            onClick={() => setSelectedCertificationsModal({certifications: assignment.certifications, assignmentId: assignment._id})}
                            className="text-xs text-brand-amber-600 font-medium hover:text-brand-amber-800 hover:underline"
                          >
                            +{assignment.certifications.length - 1} more
                          </button>
                        )}
                        {assignment.certifications && assignment.certifications.length === 1 && (
                          <button
                            onClick={() => setSelectedCertificationsModal({certifications: assignment.certifications, assignmentId: assignment._id})}
                            className="p-1 text-gray-400 hover:text-brand-amber-600 transition-colors"
                            title="View certification details"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Skipped Users */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2">
                        {/* Check if there are any skipped items */}
                        {(assignment.skipped_users?.length > 0 || assignment.skipped_groups?.length > 0 || assignment.skipped_roles?.length > 0) ? (
                          <>
                            {/* Display first skipped user */}
                            {assignment.skipped_users?.slice(0, 1).map((user: any, index: number) => (
                              <div key={index} className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-gradient-to-r from-brand-amber-500 to-brand-orange-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                  {user.user_name ? user.user_name.split(' ').map((n: string) => n[0]).join('') : '?'}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{user.user_name || 'Unknown User'}</p>
                                  <p className="text-xs text-gray-500">{user.user_email || 'N/A'}</p>
                                </div>
                              </div>
                            ))}
                            {/* Display first skipped group if no users */}
                            {!assignment.skipped_users?.length && assignment.skipped_groups?.slice(0, 1).map((group: any, index: number) => (
                              <div key={index} className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-gradient-to-r from-brand-amber-500 to-brand-orange-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                  <Users className="w-4 h-4" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{group.group_name || 'Unknown Group'}</p>
                                  <p className="text-xs text-gray-500">{group.group_key || 'N/A'}</p>
                                </div>
                              </div>
                            ))}
                            {/* Display first skipped role if no users or groups */}
                            {!assignment.skipped_users?.length && !assignment.skipped_groups?.length && assignment.skipped_roles?.slice(0, 1).map((role: any, index: number) => (
                              <div key={index} className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-gradient-to-r from-brand-amber-500 to-brand-orange-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                  <Shield className="w-4 h-4" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{role.role_name || 'Unknown Role'}</p>
                                  <p className="text-xs text-gray-500">{role.role_key || 'N/A'}</p>
                                </div>
                              </div>
                            ))}
                            {/* Show more button if there are multiple skipped items */}
                            {((assignment.skipped_users?.length || 0) + (assignment.skipped_groups?.length || 0) + (assignment.skipped_roles?.length || 0)) > 1 && (
                              <button
                                onClick={() => setSelectedSkippedUsersModal({
                                  skipped_users: assignment.skipped_users,
                                  skipped_groups: assignment.skipped_groups,
                                  skipped_roles: assignment.skipped_roles
                                })}
                                className="text-xs text-brand-amber-600 font-medium hover:text-brand-amber-800 hover:underline ml-10"
                              >
                                +{((assignment.skipped_users?.length || 0) + (assignment.skipped_groups?.length || 0) + (assignment.skipped_roles?.length || 0)) - 1} more skipped
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="text-sm text-gray-400">None</span>
                        )}
                      </div>
                    </td>

                    {/* Mandatory */}
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        assignment.is_mandatory ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      }`}>
                        {assignment.is_mandatory ? 'True' : 'False'}
                      </span>
                    </td>

                    {/* Assigned By */}
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{assignment.assigned_by_details?.name || 'Unknown'}</div>
                      <div className="text-xs text-gray-500">{assignment.assigned_by_details?.email || 'N/A'}</div>
                    </td>

                    {/* Deadline */}
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {assignment.deadline_days
                          ? `${assignment.deadline_days} ${assignment.deadline_days === '1' ? 'day' : 'days'}`
                          : 'N/A'}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        assignment.status === 'ACTIVE' ? 'bg-green-50 text-green-700' :
                        assignment.status === 'COMPLETED' ? 'bg-blue-50 text-blue-700' :
                        assignment.status === 'CANCELLED' ? 'bg-red-50 text-red-700' :
                        'bg-gray-50 text-gray-700'
                      }`}>
                        {assignment.status}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleSendReminder(assignment._id)}
                        disabled={!canWrite || actionLoading === assignment._id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={!canWrite ? 'You do not have permission to perform this action' : 'Send Reminder'}
                      >
                        {actionLoading === assignment._id ? (
                          <>
                            <div className="w-4 h-4 border-2 border-gray-300 border-t-brand-amber-600 rounded-full animate-spin" />
                            <span>Sending...</span>
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4" />
                            <span>Reminder</span>
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Assignment Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0  bg-black bg-opacity-50 flex items-center justify-center z-50 common-modal-overlay">
          <style>{`
            .modal-scroll::-webkit-scrollbar { width: 5px; }
            .modal-scroll::-webkit-scrollbar-track { background: transparent; }
            .modal-scroll::-webkit-scrollbar-thumb { background: rgba(156, 163, 175, 0.5); border-radius: 10px; }
            .modal-scroll::-webkit-scrollbar-thumb:hover { background: rgba(107, 114, 128, 0.7); }
          `}</style>
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
            <div className="p-6 max-h-[90vh] overflow-y-auto modal-scroll" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent' }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Certification Preview</h2>
              <button
                onClick={() => {
                  resetFormState();
                  setShowPreview(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Duplicate Warning Banner */}
              {duplicateCheckResult?.hasConflicts && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-medium text-yellow-900">Duplicate Assignments Detected</h3>
                      <p className="text-sm text-yellow-700 mt-1">
                        {(() => {
                          // Count unique targets (users/departments/roles), not total conflicts
                          const uniqueTargets = new Set(duplicateCheckResult.conflicts.map((c: any) => c.targetId));
                          const targetCount = uniqueTargets.size;
                          const targetLabel = assignmentType === 'department' ? 'department'
                            : assignmentType === 'role' ? 'role'
                            : 'recipient';
                          return (
                            <>
                              {targetCount} {targetLabel}{targetCount > 1 ? 's' : ''}
                              {' '}already {targetCount > 1 ? 'have' : 'has'} these certifications assigned.
                              {skipDuplicates ? ` These ${targetLabel}${targetCount > 1 ? 's' : ''} will be skipped. Only ${targetLabel}${targetCount > 1 ? 's' : ''} without conflicts will receive the certifications.` : ' This will create duplicate assignments.'}
                            </>
                          );

                        })()}
                      </p>
                      {/* Show list of targets with duplicates */}
                      
                    </div>
                  </div>
                </div>
              )}

              {/* Selected Certifications */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Selected Certifications ({selectedCertifications?.length || 0})
                  {skipDuplicates && duplicateCheckResult?.hasConflicts && (() => {
                    const duplicateCertificationIds = new Set(
                      duplicateCheckResult.conflicts.map((conflict: any) => conflict.certificationId)
                    );
                    const newCertificationsCount = selectedCertifications.filter(id => !duplicateCertificationIds.has(id)).length;
                    return (
                      <span className="ml-2 text-xs text-green-600">
                        ({newCertificationsCount} will be assigned, {duplicateCertificationIds.size} will be skipped)
                      </span>
                    );
                  })()}
                </h3>
                <div className="space-y-2">
                  {(selectedCertifications || []).map(certificationId => {
                    const certification = certifications.find(c => c.certification_id === certificationId) || selectedCertDataRef.current.get(certificationId);
                    // Check if this certification is a duplicate
                    const isDuplicate = duplicateCheckResult?.conflicts?.some(
                      (conflict: any) => conflict.certificationId === certificationId
                    );
                    const willBeSkipped = skipDuplicates && isDuplicate;

                    return certification ? (
                      <div key={certificationId} className={`flex items-center gap-3 p-3 rounded-lg ${willBeSkipped ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'}`}>
                        {certification.thumbnail_image_url ? (
                          <img
                            src={certification.thumbnail_image_url}
                            alt={certification.title}
                            className="w-12 h-12 rounded-lg object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <div className={`w-12 h-12 bg-gradient-to-br from-brand-amber-500 to-brand-amber-600 rounded-lg flex items-center justify-center text-white ${certification.thumbnail_image_url ? 'hidden' : ''}`}>
                          <Award className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{certification.title}</p>
                          <p className="text-xs text-gray-500">
                            {certification.time_limit ? `${certification.time_limit}m` : 'N/A'} • {certification.difficulty?.toUpperCase() || 'N/A'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {willBeSkipped ? (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Will be skipped
                            </span>
                          ) : isDuplicate && !skipDuplicates ? (
                            <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                              Duplicate
                            </span>
                          ) : null}
                          {assignmentSettings.is_mandatory && (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                              Mandatory
                            </span>
                          )}
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>

              {/* Recipients */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  {assignmentType === 'department' ? 'Departments' : assignmentType === 'role' ? 'Roles' : 'Recipients'}
                  {duplicateCheckResult?.hasConflicts && (() => {
                    // Count unique targets (users/departments/roles), not total conflicts
                    const uniqueTargets = new Set(duplicateCheckResult.conflicts.map((c: any) => c.targetId));
                    const targetCount = uniqueTargets.size;
                    const targetLabel = assignmentType === 'department' ? 'department' : assignmentType === 'role' ? 'role' : 'recipient';
                    return (
                      <span className="ml-2 text-xs font-semibold text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full border border-yellow-200">
                        {targetCount} {targetLabel}{targetCount > 1 ? 's' : ''} {targetCount > 1 ? 'have' : 'has'} duplicate certifications
                      </span>
                    );
                  })()}
                </h3>
                {assignmentType === 'department' ? (
                  duplicateCheckResult && duplicateCheckResult.hasConflicts ? (
                    <>
                      {/* Departments without conflicts */}
                      {selectedDepartments.filter(deptId => {
                        const hasConflict = duplicateCheckResult?.conflicts?.some(
                          (conflict: any) => conflict.targetId === deptId
                        );
                        return !hasConflict;
                      }).length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                              ✅
                            </div>
                            New Assignments (No Duplicates)
                          </p>
                          <div className="space-y-2">
                            {selectedDepartments.filter(deptId => {
                              const hasConflict = duplicateCheckResult?.conflicts?.some(
                                (conflict: any) => conflict.targetId === deptId
                              );
                              return !hasConflict;
                            }).map(deptId => {
                              const dept = groups.find(d => d._id === deptId);
                              return dept ? (
                                <div key={deptId} className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                                      <Building className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div className="flex-1">
                                      <div className="font-medium text-gray-900">{dept.group_name}</div>
                                      <div className="text-sm text-gray-600 mt-0.5">Department</div>
                                    </div>
                                    <div className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded">
                                      All New
                                    </div>
                                  </div>
                                </div>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}

                      {/* Departments with conflicts */}
                      {duplicateCheckResult.conflicts?.length > 0 && selectedDepartments.filter(deptId => {
                        const hasConflict = duplicateCheckResult?.conflicts?.some(
                          (conflict: any) => conflict.targetId === deptId
                        );
                        return hasConflict;
                      }).length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-semibold text-yellow-700 mb-3 flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-yellow-100 flex items-center justify-center">
                              ⚠️
                            </div>
                            Has Duplicate Certifications (Will receive only NEW certifications)
                          </p>
                          <div className="space-y-2">
                            {selectedDepartments.filter(deptId => {
                              const hasConflict = duplicateCheckResult?.conflicts?.some(
                                (conflict: any) => conflict.targetId === deptId
                              );
                              return hasConflict;
                            }).map(deptId => {
                              const dept = groups.find(d => d._id === deptId);
                              if (!dept) return null;

                              const deptConflicts = duplicateCheckResult.conflicts.filter(
                                (conflict: any) => conflict.targetId === deptId
                              );
                              const duplicateCertificationNames = deptConflicts.map((conflict: any) => {
                                const certification = certifications.find(c => c.certification_id === conflict.certificationId) || selectedCertDataRef.current.get(conflict.certificationId);
                                return certification?.title || 'Unknown Certification';
                              });
                              const duplicateCount = deptConflicts.length;
                              const totalCertifications = selectedCertifications.length;
                              const newCertificationsCount = totalCertifications - duplicateCount;

                              return (
                                <div key={deptId} className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                                  <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                                      <Building className="w-5 h-5 text-yellow-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-gray-900">{dept.group_name}</div>
                                      <div className="text-sm text-gray-600 mt-0.5">Department</div>
                                      <div className="mt-2 space-y-1">
                                        <div className="text-xs text-yellow-800">
                                          <span className="font-semibold">Duplicate certifications ({duplicateCount}):</span>
                                          <div className="mt-1 text-yellow-700">{duplicateCertificationNames.join(', ')}</div>
                                        </div>
                                        {newCertificationsCount > 0 && (
                                          <div className="text-xs text-green-700 font-medium">
                                            ✓ Will receive {newCertificationsCount} new certification{newCertificationsCount > 1 ? 's' : ''}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-xs font-medium text-yellow-700 bg-yellow-100 px-2 py-1 rounded flex-shrink-0">
                                      {duplicateCount}/{totalCertifications} Duplicate
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {selectedDepartments.map(deptId  => {
                        const dept = groups.find(d => d._id === deptId);
                        return dept ? (
                          <div key={deptId} className="px-3 py-2 bg-primary-100 text-primary-700 rounded-lg text-sm font-medium">
                            <Building className="w-3 h-3 inline mr-1" />
                            {dept.group_name}
                          </div>
                        ) : null;
                      })}
                    </div>
                  )
                ) : assignmentType === 'role' ? (
                  duplicateCheckResult && duplicateCheckResult.hasConflicts ? (
                    <>
                      {/* Roles without conflicts */}
                      {selectedRoles.filter(roleObj => {
                        const roleId = `${roleObj.group_key}_${roleObj.role_key}`;
                        const hasConflict = duplicateCheckResult?.conflicts?.some(
                          (conflict: any) => conflict.targetId === roleId
                        );
                        return !hasConflict;
                      }).length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                              ✅
                            </div>
                            New Assignments (No Duplicates)
                          </p>
                          <div className="space-y-2">
                            {selectedRoles.filter(roleObj => {
                              const roleId = `${roleObj.group_key}_${roleObj.role_key}`;
                              const hasConflict = duplicateCheckResult?.conflicts?.some(
                                (conflict: any) => conflict.targetId === roleId
                              );
                              return !hasConflict;
                            }).map((roleObj, index) => {
                              const deptName = availableDepartments.find(d => d.group_key === roleObj.group_key)?.group_name || roleObj.group_key;
                              return (
                                <div key={`${roleObj.group_key}-${roleObj.role_key}-${index}`} className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                                      <Briefcase className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div className="flex-1">
                                      <div className="font-medium text-gray-900">{roleObj.role_name || roleObj.role_key}</div>
                                      <div className="text-sm text-gray-600 flex items-center gap-1 mt-0.5">
                                        <Building className="w-3 h-3" />
                                        {deptName}
                                      </div>
                                    </div>
                                    <div className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded">
                                      All New
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Roles with conflicts */}
                      {duplicateCheckResult.conflicts?.length > 0 && selectedRoles.filter(roleObj => {
                        const roleId = `${roleObj.group_key}_${roleObj.role_key}`;
                        const hasConflict = duplicateCheckResult?.conflicts?.some(
                          (conflict: any) => conflict.targetId === roleId
                        );
                        return hasConflict;
                      }).length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-semibold text-yellow-700 mb-3 flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-yellow-100 flex items-center justify-center">
                              ⚠️
                            </div>
                            Has Duplicate Certifications (Will receive only NEW certifications)
                          </p>
                          <div className="space-y-2">
                            {selectedRoles.filter(roleObj => {
                              const roleId = `${roleObj.group_key}_${roleObj.role_key}`;
                              const hasConflict = duplicateCheckResult?.conflicts?.some(
                                (conflict: any) => conflict.targetId === roleId
                              );
                              return hasConflict;
                            }).map((roleObj, index) => {
                              const roleId = `${roleObj.group_key}_${roleObj.role_key}`;
                              const roleConflicts = duplicateCheckResult.conflicts.filter(
                                (conflict: any) => conflict.targetId === roleId
                              );
                              const duplicateCertificationNames = roleConflicts.map((conflict: any) => {
                                const certification = certifications.find(c => c.certification_id === conflict.certificationId) || selectedCertDataRef.current.get(conflict.certificationId);
                                return certification?.title || 'Unknown Certification';
                              });
                              const deptName = availableDepartments.find(d => d.group_key === roleObj.group_key)?.group_name || roleObj.group_key;
                              const duplicateCount = roleConflicts.length;
                              const totalCertifications = selectedCertifications.length;
                              const newCertificationsCount = totalCertifications - duplicateCount;

                              return (
                                <div key={`${roleObj.group_key}-${roleObj.role_key}-${index}`} className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                                  <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                                      <Briefcase className="w-5 h-5 text-yellow-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-gray-900">{roleObj.role_name || roleObj.role_key}</div>
                                      <div className="text-sm text-gray-600 flex items-center gap-1 mt-0.5">
                                        <Building className="w-3 h-3" />
                                        {deptName}
                                      </div>
                                      <div className="mt-2 space-y-1">
                                        <div className="text-xs text-yellow-800">
                                          <span className="font-semibold">Duplicate certifications ({duplicateCount}):</span>
                                          <div className="mt-1 text-yellow-700">{duplicateCertificationNames.join(', ')}</div>
                                        </div>
                                        {newCertificationsCount > 0 && (
                                          <div className="text-xs text-green-700 font-medium">
                                            ✓ Will receive {newCertificationsCount} new certification{newCertificationsCount > 1 ? 's' : ''}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-xs font-medium text-yellow-700 bg-yellow-100 px-2 py-1 rounded flex-shrink-0">
                                      {duplicateCount}/{totalCertifications} Duplicate
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {selectedRoles.map((roleObj, index) => {
                        return (
                          <div key={`${roleObj.group_key}-${roleObj.role_key}-${index}`} className="px-3 py-2 bg-primary-100 text-primary-700 rounded-lg text-sm font-medium">
                            <Briefcase className="w-3 h-3 inline mr-1" />
                            {roleObj.role_name || roleObj.role_key}
                          </div>
                        );
                      })}
                    </div>
                  )
                ) : (
                  <div>
                    {/* Show categorized users if duplicate check was performed and conflicts exist */}
                    {duplicateCheckResult && duplicateCheckResult.hasConflicts ? (
                      <>
                        {/* Users without conflicts */}
                        <div className="mb-3">
                          <p className="text-xs font-medium text-green-700 mb-2">✅ New Assignments</p>
                          <div className="flex flex-wrap gap-2">
                            {selectedUsers.filter(userId => {
                              // Check if this user has conflicts
                              const hasConflict = duplicateCheckResult?.conflicts?.some(
                                (conflict: any) => conflict.targetId === userId
                              );
                              return !hasConflict;
                            }).map(userId => {
                              const user = getSelectedUser(userId);
                              return user ? (
                                <div key={userId} className="px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm border border-green-200">
                                  <UserIcon className="w-3 h-3 inline mr-1" />
                                  {user.given_name} {user.family_name || ''}
                                </div>
                              ) : null;
                            })}
                          </div>
                        </div>
                      </>
                    ) : (
                      /* Show all users normally if no conflicts or check not performed */
                      <div className="flex flex-wrap gap-2">
                        {selectedUsers.map(userId => {
                          const user = getSelectedUser(userId);
                          return user ? (
                            <div key={userId} className="px-3 py-2 bg-primary-100 text-primary-700 rounded-lg text-sm font-medium">
                              <UserIcon className="w-3 h-3 inline mr-1" />
                              {user.given_name} {user.family_name || ''}
                            </div>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Assignment Settings */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Assignment Settings</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="mandatory"
                      className="rounded border-gray-300"
                      checked={assignmentSettings.is_mandatory}
                      onChange={(e) => setAssignmentSettings(prev => ({ ...prev, is_mandatory: e.target.checked }))}
                    />
                    <label htmlFor="mandatory" className="text-sm text-gray-700">Mark as mandatory</label>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Set Deadline</label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <input
                          type="number"
                          min="1"
                          placeholder="Enter days"
                          className={`w-full px-4 py-2 pr-16 border rounded-lg text-sm ${
                            deadlineError ? 'border-red-500' : 'border-gray-300'
                          }`}
                          value={deadlineDays !== undefined ? deadlineDays : ''}
                          onChange={handleDeadlineDaysChange}
                          onBlur={handleDeadlineDaysBlur}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                          days
                        </span>
                      </div>
                      {deadlineDays && deadlineDays > 0 && !deadlineError && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-brand-blue-50 border border-brand-blue-200 rounded-lg">
                          <Calendar className="w-4 h-4 text-brand-blue-600" />
                          <div>
                            <p className="text-xs text-brand-blue-600 font-medium">
                              {new Date(new Date().setDate(new Date().getDate() + deadlineDays))
                                .toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    {deadlineError && (
                      <div className="flex items-center gap-1 text-red-600">
                        <AlertCircle className="w-3 h-3" />
                        <p className="text-xs">{deadlineError}</p>
                      </div>
                    )}
                    {!deadlineError && deadlineDays && deadlineDays > 0 && (
                      <p className="text-xs text-gray-500">
                        Assignment will be due in {deadlineDays} {deadlineDays === 1 ? 'day' : 'days'} from now
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="notify"
                      className="rounded border-gray-300"
                      checked={assignmentSettings.send_notification}
                      onChange={(e) => setAssignmentSettings(prev => ({ ...prev, send_notification: e.target.checked }))}
                    />
                    <label htmlFor="notify" className="text-sm text-gray-700">Send email notification</label>
                  </div>
                  {assignmentSettings.send_notification && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="reminder"
                          className="rounded border-gray-300"
                          checked={assignmentSettings.enable_automatic_reminder}
                          onChange={(e) => setAssignmentSettings(prev => ({ ...prev, enable_automatic_reminder: e.target.checked }))}
                        />
                        <label htmlFor="reminder" className="text-sm text-gray-700">Enable automatic reminders</label>
                      </div>
                      {assignmentSettings.enable_automatic_reminder && (
                        <div className="ml-6 p-3 bg-brand-blue-50 border border-brand-blue-200 rounded-lg space-y-3">
                          <div className="flex items-center gap-3">
                            <label className="text-sm font-medium text-gray-700">Reminder Frequency:</label>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-600">Every</span>
                              <input
                                type="number"
                                min="1"
                                className="w-16 px-2 py-1.5 text-sm border border-brand-blue-300 rounded-md text-center focus:outline-none focus:ring-2 focus:ring-brand-blue-500 bg-white"
                                value={assignmentSettings.reminder_frequency_days || 7}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value) || 1;
                                  setAssignmentSettings(prev => ({ ...prev, reminder_frequency_days: value > 0 ? value : 1 }));
                                }}
                              />
                              <span className="text-sm text-gray-600">day{(assignmentSettings.reminder_frequency_days || 7) !== 1 ? 's' : ''}</span>
                            </div>
                          </div>
                          <div className="flex items-start gap-2 text-xs text-brand-blue-700">
                            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <p>Reminder emails will be sent automatically every {assignmentSettings.reminder_frequency_days || 7} day{(assignmentSettings.reminder_frequency_days || 7) !== 1 ? 's' : ''} to users who haven't completed the assigned certification until the deadline or completion.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Message */}
              <Textarea<FilterFormData>
                name="assignmentMessage"
                control={control}
                label="Message (Optional)"
                rows={3}
                placeholder="Add a message for the recipients..."
              />
            </div>

            <div className="flex gap-3 justify-end mt-6 pt-4 border-t">
              <button
                onClick={() => {
                  resetFormState();
                  setShowPreview(false);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignCertifications}
                disabled={isAssigning || !!deadlineError}
                className="flex items-center gap-2 px-4 py-2 bg-brand-amber-600 text-white rounded-lg hover:bg-brand-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                {isAssigning ? 'Assigning...' : 'Assign Certifications'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      <CertificationBulkImportModal
        isOpen={showBulkImportModal}
        onClose={() => {
          setShowBulkImportModal(false);
          // Stay on bulk tab when closing modal
        }}
        onImportSuccess={handleBulkImportSuccess}
      />

      {/* Assignment Details Modal */}
      <Modal
        isOpen={showAssignmentDetails && !!selectedAssignment}
        onClose={() => {
          setShowAssignmentDetails(false);
          setSelectedAssignment(null);
        }}
        title="Assignment Details"
        size="lg"
        maxHeight="90vh"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setShowAssignmentDetails(false);
                setSelectedAssignment(null);
              }}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedAssignment && (
          <div className="space-y-4">
              {/* Assignment Type */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Type:</span>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  selectedAssignment.assignment_type === 'USER' ? 'bg-blue-100 text-blue-700' :
                  selectedAssignment.assignment_type === 'GROUP' ? 'bg-purple-100 text-purple-700' :
                  selectedAssignment.assignment_type === 'ROLE' ? 'bg-green-100 text-green-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {selectedAssignment.assignment_type === 'USER' ? <UserIcon className="w-3 h-3 mr-1" /> :
                   selectedAssignment.assignment_type === 'GROUP' ? <Building className="w-3 h-3 mr-1" /> :
                   selectedAssignment.assignment_type === 'ROLE' ? <Briefcase className="w-3 h-3 mr-1" /> : null}
                  {selectedAssignment.assignment_type}
                </span>
              </div>

              {/* Target Info */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Assignment Details:</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Assigned By:</span>
                    <span className="text-sm text-gray-900">
                      {selectedAssignment.assigned_by_details
                        ? `${selectedAssignment.assigned_by_details.given_name || ''} ${selectedAssignment.assigned_by_details.family_name || ''}`.trim()
                        : 'Unknown'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Assigned Date:</span>
                    <span className="text-sm text-gray-900">
                      {selectedAssignment.assigned_date ? new Date(selectedAssignment.assigned_date).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  
                </div>
              </div>

              {/* Certifications Assigned */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Certifications Assigned:</h3>
                <div className="space-y-2">
                  {selectedAssignment.certification_details && selectedAssignment.certification_details.length > 0 ? (
                    selectedAssignment.certification_details.map((cert: any, idx: number) => (
                      <div key={cert._id || idx} className="flex items-center gap-2">
                        <Award className="w-4 h-4 text-brand-amber-500" />
                        <span className="text-sm text-gray-900 font-medium">{cert.certification_name}</span>
                      </div>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">No certifications assigned</span>
                  )}
                </div>
              </div>

              {/* Progress Statistics */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Progress Statistics:</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Total Assignees</div>
                    <div className="text-lg font-semibold text-gray-900">{selectedAssignment.total_assignees || 0}</div>
                  </div>
                </div>
                  
              </div>

              {/* Settings */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Settings:</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Mandatory:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedAssignment.is_mandatory ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {selectedAssignment.is_mandatory ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Send Notifications:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedAssignment.send_notification ? 'bg-brand-blue-100 text-brand-blue-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {selectedAssignment.send_notification ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Auto Reminders:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedAssignment.enable_automatic_reminder ? 'bg-brand-blue-100 text-brand-blue-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {selectedAssignment.enable_automatic_reminder
                        ? `Every ${selectedAssignment.reminder_frequency_days || 7} days`
                        : 'Disabled'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedAssignment.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                      selectedAssignment.status === 'COMPLETED' ? 'bg-blue-100 text-blue-700' :
                      selectedAssignment.status === 'CANCELLED' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {selectedAssignment.status || 'UNKNOWN'}
                    </span>
                  </div>
                  {selectedAssignment.deadline_days && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Deadline:</span>
                      <span className="text-sm text-gray-900">
                        {selectedAssignment.deadline_days} {selectedAssignment.deadline_days === 1 ? 'day' : 'days'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Timestamps */}
              <div className="border-t pt-4 space-y-1 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Created: {new Date(selectedAssignment.created_at || Date.now()).toLocaleString()}</span>
                </div>
                {selectedAssignment.updated_at && selectedAssignment.updated_at !== selectedAssignment.created_at && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>Updated: {new Date(selectedAssignment.updated_at).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          )}
      </Modal>

      {/* Certification Details Modal */}
      <Modal
        isOpen={!!selectedCertificationsModal}
        onClose={() => setSelectedCertificationsModal(null)}
        title="Assigned Certifications"
        subtitle={selectedCertificationsModal ? `${selectedCertificationsModal.certifications.length} certification${selectedCertificationsModal.certifications.length !== 1 ? 's' : ''} assigned` : ''}
        size="lg"
        maxHeight="80vh"
        footer={
          <div className="flex items-center justify-end">
            <button
              onClick={() => setSelectedCertificationsModal(null)}
              className="px-4 py-2 bg-brand-amber-600 text-white rounded-lg hover:bg-brand-amber-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          {selectedCertificationsModal?.certifications.map((certification, idx) => (
            <div key={certification._id} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="flex-shrink-0 w-8 h-8 bg-brand-amber-100 text-brand-amber-600 rounded-full flex items-center justify-center font-semibold text-sm">
                {idx + 1}
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-900">{certification.name}</h3>
              </div>
              <Award className="w-5 h-5 text-gray-400" />
            </div>
          ))}
        </div>
      </Modal>

      {/* User Departments & Roles Modal */}
      <Modal
        isOpen={!!selectedUserDepartments}
        onClose={() => setSelectedUserDepartments(null)}
        title="Department & Role Details"
        subtitle={selectedUserDepartments ? `${selectedUserDepartments.userName} (${selectedUserDepartments.email})` : ''}
        size="lg"
        maxHeight="80vh"
        footer={
          <div className="flex items-center justify-end">
            <button
              onClick={() => setSelectedUserDepartments(null)}
              className="px-4 py-2 bg-brand-amber-600 text-white rounded-lg hover:bg-brand-amber-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedUserDepartments && (
          <div className="space-y-4">
            {selectedUserDepartments.groups.map((group, idx) => (
              <div key={`${group.group_key}-${idx}`} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-100 px-4 py-3 flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900">{group.group_name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{group.group_key}</p>
                  </div>
                  <Building className="w-5 h-5 text-gray-500" />
                </div>
                {group.roles && group.roles.length > 0 && (
                  <div className="bg-white px-4 py-3">
                    <p className="text-xs font-medium text-gray-700 mb-2">Roles in this department:</p>
                    <div className="space-y-2">
                      {group.roles.map((role, roleIdx) => (
                        <div key={`${role.role_key}-${roleIdx}`} className="flex items-center gap-2 text-sm">
                          <div className="w-6 h-6 bg-brand-amber-100 text-brand-amber-600 rounded-full flex items-center justify-center">
                            <Briefcase className="w-3 h-3" />
                          </div>
                          <span className="text-gray-900">{role.role_name}</span>
                          <span className="text-xs text-gray-500">({role.role_key})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Assignment"
        description="Are you sure you want to delete this assignment? This action will permanently remove the assignment and cannot be undone."
        itemName={assignmentToDelete?.title}
        isLoading={actionLoading === assignmentToDelete?.id}
      />

      {/* Assigned Users/Groups/Roles Modal for Recent Assignments */}
      <Modal
        isOpen={!!selectedAssignedUsersModal}
        onClose={() => setSelectedAssignedUsersModal(null)}
        title={
          selectedAssignedUsersModal?.type === 'USER' ? 'Assigned Users' :
          selectedAssignedUsersModal?.type === 'GROUP' ? 'Assigned Departments' :
          'Assigned Roles'
        }
        subtitle={
          selectedAssignedUsersModal?.type === 'USER'
            ? `${selectedAssignedUsersModal?.users?.length || 0} user${(selectedAssignedUsersModal?.users?.length || 0) !== 1 ? 's' : ''} assigned`
            : selectedAssignedUsersModal?.type === 'GROUP'
            ? `${selectedAssignedUsersModal?.groups?.length || 0} department${(selectedAssignedUsersModal?.groups?.length || 0) !== 1 ? 's' : ''} assigned`
            : `${selectedAssignedUsersModal?.roles?.length || 0} role${(selectedAssignedUsersModal?.roles?.length || 0) !== 1 ? 's' : ''} assigned`
        }
        size="lg"
        maxHeight="80vh"
        footer={
          <div className="flex items-center justify-end">
            <button
              onClick={() => setSelectedAssignedUsersModal(null)}
              className="px-4 py-2 bg-brand-amber-600 text-white rounded-lg hover:bg-brand-amber-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedAssignedUsersModal && (
          <div className="space-y-3">
            {/* Users List */}
            {selectedAssignedUsersModal.type === 'USER' && selectedAssignedUsersModal.users?.map((user: any) => (
              <div key={user._id} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-indigo-500 to-brand-purple-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                  {user.name ? user.name.split(' ').map((n: string) => n[0]).join('') : '?'}
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{user.name || 'Unknown User'}</h3>
                  <p className="text-xs text-gray-500">{user.email || 'N/A'}</p>
                </div>
                <UserIcon className="w-5 h-5 text-gray-400" />
              </div>
            ))}

            {/* Groups List */}
            {selectedAssignedUsersModal.type === 'GROUP' && selectedAssignedUsersModal.groups?.map((group: any) => (
              <div key={group._id || group.group_key} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-blue-500 to-brand-cyan-500 rounded-full flex items-center justify-center text-white">
                  <Users className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{group.name || group.group_name || 'Unknown Group'}</h3>
                  <p className="text-xs text-gray-500">{group.group_key || 'N/A'}</p>
                </div>
                <Users className="w-5 h-5 text-gray-400" />
              </div>
            ))}

            {/* Roles List */}
            {selectedAssignedUsersModal.type === 'ROLE' && selectedAssignedUsersModal.roles?.map((role: any) => (
              <div key={role._id || role.role_key} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-amber-500 to-brand-orange-500 rounded-full flex items-center justify-center text-white">
                  <Shield className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{role.name || role.role_name || 'Unknown Role'}</h3>
                  <p className="text-xs text-gray-500">{role.role_key || 'N/A'}</p>
                </div>
                <Shield className="w-5 h-5 text-gray-400" />
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Skipped Users/Groups/Roles Modal */}
      <Modal
        isOpen={!!selectedSkippedUsersModal}
        onClose={() => setSelectedSkippedUsersModal(null)}
        title="Skipped Assignments"
        subtitle={`${(selectedSkippedUsersModal?.skipped_users?.length || 0) + (selectedSkippedUsersModal?.skipped_groups?.length || 0) + (selectedSkippedUsersModal?.skipped_roles?.length || 0)} item${((selectedSkippedUsersModal?.skipped_users?.length || 0) + (selectedSkippedUsersModal?.skipped_groups?.length || 0) + (selectedSkippedUsersModal?.skipped_roles?.length || 0)) !== 1 ? 's' : ''} skipped due to existing assignments`}
        size="lg"
        maxHeight="80vh"
        footer={
          <div className="flex items-center justify-end">
            <button
              onClick={() => setSelectedSkippedUsersModal(null)}
              className="px-4 py-2 bg-brand-amber-600 text-white rounded-lg hover:bg-brand-amber-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedSkippedUsersModal && (
          <div className="space-y-3">
            {/* Skipped Users List */}
            {selectedSkippedUsersModal.skipped_users?.map((user: any, index: number) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-amber-500 to-brand-orange-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                  {user.user_name ? user.user_name.split(' ').map((n: string) => n[0]).join('') : '?'}
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{user.user_name || 'Unknown User'}</h3>
                  <p className="text-xs text-gray-500">{user.user_email || 'N/A'}</p>
                  <p className="text-xs text-amber-600 mt-1">{user.reason}</p>
                </div>
                <XCircle className="w-5 h-5 text-amber-500" />
              </div>
            ))}

            {/* Skipped Groups List */}
            {selectedSkippedUsersModal.skipped_groups?.map((group: any, index: number) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-amber-500 to-brand-orange-500 rounded-full flex items-center justify-center text-white">
                  <Users className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{group.group_name || 'Unknown Group'}</h3>
                  <p className="text-xs text-gray-500">{group.group_key || 'N/A'}</p>
                  <p className="text-xs text-amber-600 mt-1">{group.reason}</p>
                </div>
                <XCircle className="w-5 h-5 text-amber-500" />
              </div>
            ))}

            {/* Skipped Roles List */}
            {selectedSkippedUsersModal.skipped_roles?.map((role: any, index: number) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-amber-500 to-brand-orange-500 rounded-full flex items-center justify-center text-white">
                  <Shield className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{role.role_name || 'Unknown Role'}</h3>
                  <p className="text-xs text-gray-500">{role.role_key || 'N/A'}</p>
                  <p className="text-xs text-amber-600 mt-1">{role.reason}</p>
                </div>
                <XCircle className="w-5 h-5 text-amber-500" />
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
